/**
 *  - TABLE_QAM_ACTIVITIES: unique SmartTable key for the activities list
 *  - generateColumns: builds the column configuration array for the activities table
 *  - downloadActivityReportPdf: triggers a PDF export for a single activity
 *  - downloadActivityReportZip: triggers a ZIP export (PDF + attachments) for a single activity
 *  - getActivityStageLangKeyName: maps a stage name string to its i18n translation key
 *  - replaceLocalstorageFilters: resolves the active stage filter from URL state vs. localStorage
 */
import { openModalAction } from "../../../components/Modal/reducer";
import { MODAL_EXPORT_LOADING } from "../../../components/SmartTable/ExportDropdown/LoadingModal";
import axios from "../../../axios";
import { EXPORT_TYPE_PDF, EXPORT_TYPE_ZIP } from "../../../components/SmartTable/ExportDropdown/ExportOverlay";
import { handleExportDownload, handleExportError } from "../../../components/SmartTable/ExportDropdown/methods";

export { generateColumns } from "./activityColumns";

/** Unique key used to identify the QAM activities SmartTable in Redux and localStorage */
export const TABLE_QAM_ACTIVITIES = "table-qam-activities";

/**
 * Initiates a PDF export for a single QAM activity and triggers a file download.
 *
 * Shows the global export-loading modal while the request is in progress, then downloads
 * the generated file from the signed URL returned by the backend. Closes the modal on both
 * success and failure, and shows an error notification if the download or request fails.
 *
 * @param {Function} trans      - i18n translation function for error messages
 * @param {Function} dispatch   - Redux dispatch function
 * @param {object}   row        - The activity row object; needs at minimum `id` and `title`
 * @param {string}   exportType - Export sub-type passed to the backend (e.g. report variant)
 */
export const downloadActivityReportPdf = (trans, dispatch, row, exportType) => {
  dispatch(openModalAction({ name: MODAL_EXPORT_LOADING }));
  axios
    .get(`qam/audits/export/${row.id}`, {
      params: {
        exportType: exportType,
        filename: row.title,
        type: EXPORT_TYPE_PDF
      }
    })
    .then(
      (res) => {
        handleExportDownload(res, dispatch, trans);
      },
      () => handleExportError(dispatch, trans)
    );
};

/**
 * Initiates a ZIP export (PDF report bundled with attachments) for a single QAM activity.
 *
 * Behaves identically to `downloadActivityReportPdf` but calls the `zip-export` endpoint
 * and requests the EXPORT_TYPE_ZIP format so the backend packages attachments alongside
 * the generated PDF.
 *
 * @param {Function} trans      - i18n translation function for error messages
 * @param {Function} dispatch   - Redux dispatch function
 * @param {object}   row        - The activity row object; needs at minimum `id` and `title`
 * @param {string}   exportType - Export sub-type passed to the backend
 */
export const downloadActivityReportZip = (trans, dispatch, row, exportType) => {
  dispatch(openModalAction({ name: MODAL_EXPORT_LOADING }));
  axios
    .get(`qam/audits/zip-export/${row.id}`, {
      params: {
        exportType: exportType,
        filename: row.title,
        type: EXPORT_TYPE_ZIP
      }
    })
    .then(
      (res) => {
        handleExportDownload(res, dispatch, trans);
      },
      () => handleExportError(dispatch, trans)
    );
};

/**
 * Maps a QAM activity stage name (as stored in the database) to its i18n translation key.
 *
 * Stage names on the backend use English title-case strings. This function provides the
 * indirection needed to render them in the user's active language via react-i18next.
 * Returns null for unknown stage names so callers can fall back to a dash "-" indicator.
 *
 * @param {string} name - The stage name string from the API (e.g. "Planning", "Closed")
 * @returns {string|null} The i18n key for the stage label, or null if unrecognised
 */
export const getActivityStageLangKeyName = (name) => {
  let toReturn = "";
  switch (name) {
    case "Planning":
      toReturn = "qam.general_words.qam_activity_stages.planning";
      break;
    case "Preparation":
      toReturn = "qam.general_words.qam_activity_stages.preparation";
      break;
    case "Execution":
      toReturn = "qam.general_words.qam_activity_stages.execution";
      break;
    case "Reporting":
      toReturn = "qam.general_words.qam_activity_stages.reporting";
      break;
    case "Surveillance Finding":
      toReturn = "qam.general_words.qam_activity_stages.surveillance_finding";
      break;
    case "Closed":
      toReturn = "qam.general_words.qam_activity_stages.closed";
      break;
    default:
      toReturn = null;
      break;
  }
  return toReturn;
};

/**
 * Resolves the initial filter set for the activities table, giving priority to a stage filter
 * that may have been passed via component props (e.g. from the QAM dashboard "quick filter" links)
 * over the filters previously persisted in localStorage.
 *
 * When a stageFilter is present it completely replaces any saved filters so the table
 * opens in the correct stage view regardless of what was last stored.
 *
 * @param {object}      localstorageFilters - Filters previously saved to localStorage for this table
 * @param {object|null} stageFilter         - A stage filter object from external navigation state,
 *                                            or null/undefined if no stage was pre-selected
 * @returns {{ filters?: object[] }} Object with a `filters` key to merge into TableService state,
 *                                   or the raw localstorageFilters when no stageFilter is provided
 */
export const replaceLocalstorageFilters = (localstorageFilters, stageFilter) => {
  const stgFilters = stageFilter && [
    {
      column: "stage_name",
      op: "eq",
      id: stageFilter.id,
      value: stageFilter.value,
      name: stageFilter.name,
      text: stageFilter.name,
      transKey: stageFilter.transKey
    }
  ];

  return {
    ...(stageFilter ? { filters: stgFilters } : localstorageFilters)
  };
};
